"use client";

import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Project = {
  title: string;
  description: string;
};
type Lab = {
  code: string;
  name: string;
  description: string;
  pi: string;
  established: string;
  funding: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
  projects: Project[];
  accentVar: string;
};
const labs: Lab[] = [{
  code: "[SERVICE-01 / INTERIOR PRECISION]",
  name: "Interior Residential Painting",
  description: "Transforming your living spaces with meticulous attention to detail. We specialize in color consultation, surface preparation, and flawless finishes that breathe new life into every room.",
  pi: "PREMIUM FINISHES",
  established: "ESTABLISHED 2012",
  funding: "FULL WARRANTY",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/interior_painting_detail.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/interior_painting_detail.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/interior_painting_detail.png-wvc-sizes",
  alt: "Professional painter applying smooth interior finish",
  projects: [{
    title: "Custom Color Matching",
    description: "Finding the perfect palette for your unique home aesthetic."
  }, {
    title: "Wall Repair & Prep",
    description: "Ensuring a smooth, durable canvas for every coat."
  }, {
    title: "Trim & Molding Detail",
    description: "Precision work for architectural highlights."
  }, {
    title: "Eco-Friendly Coatings",
    description: "Low-VOC paints for a healthier home environment."
  }],
  accentVar: "var(--chart-1)"
}, {
  code: "[SERVICE-02 / EXTERIOR PROTECTION]",
  name: "Exterior Residential Painting",
  description: "Protecting your home against the elements with high-performance coatings. Our exterior services combine aesthetic curb appeal with long-lasting structural defense.",
  pi: "WEATHER RESISTANT",
  established: "ESTABLISHED 2012",
  funding: "DURABLE SHIELD",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/exterior_home_painting.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/exterior_home_painting.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/exterior_home_painting.png-wvc-sizes",
  alt: "Exterior home painting project in progress",
  projects: [{
    title: "Weather-Proofing",
    description: "Advanced sealants to prevent moisture damage."
  }, {
    title: "Surface Restoration",
    description: "Power washing and wood repair before painting."
  }, {
    title: "Curb Appeal Upgrades",
    description: "Modern color schemes to elevate property value."
  }, {
    title: "Deck & Fence Staining",
    description: "Preserving outdoor wood structures with care."
  }],
  accentVar: "var(--chart-2)"
}];
function LabModule({
  lab,
  index,
  total
}: {
  lab: Lab;
  index: number;
  total: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.15
  });
  const isImageLeft = index % 2 === 0;
  const moduleNumber = String(index + 1).padStart(2, "0");
  const totalNumber = String(total).padStart(2, "0");
  return <div ref={ref} data-index={index} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <div className={`grid grid-cols-1 lg:grid-cols-2 lg:min-h-[600px] ${isImageLeft ? "" : "lg:[&>div:first-child]:order-2"}`}>
        {}
        <div className="relative overflow-hidden bg-muted min-h-[320px] lg:min-h-[600px]">
          <img src={lab.image} data-wvc-srcset={lab.srcset} data-wvc-sizes={lab.sizes} alt={lab.alt} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover grayscale contrast-110 mix-blend-luminosity opacity-90 scale-110 transition-transform duration-[1200ms] ease-out hover:scale-105" style={{
          filter: "grayscale(1) contrast(1.1) brightness(0.85)"
        }} />
          <div className="absolute inset-0 mix-blend-color opacity-40" style={{
          backgroundColor: lab.accentVar
        }} aria-hidden="true" />
          <div className="absolute inset-0 opacity-60" style={{
          background: "linear-gradient(135deg, hsla(220, 15%, 6%, 0.5) 0%, hsla(220, 15%, 6%, 0.1) 50%, hsla(220, 15%, 6%, 0.4) 100%)"
        }} aria-hidden="true" />
          <div className="absolute top-6 left-6 font-mono text-xs tracking-wider text-foreground/80">
            {moduleNumber} / {totalNumber}
          </div>
        </div>

        {}
        <div className="flex items-center px-6 sm:px-10 lg:px-16 py-12 lg:py-16 bg-background">
          <div className="w-full max-w-xl">
            <div className="font-mono text-xs tracking-wider mb-6 sm:mb-8" style={{
            color: lab.accentVar
          }}>
              {lab.code}
            </div>

            <h3 className="font-serif font-default font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground mb-6 sm:mb-8">
              {lab.name}
            </h3>

            <p className="text-[17px] leading-relaxed text-muted-foreground mb-6 sm:mb-8 max-w-md">
              {lab.description}
            </p>

            <div className="font-mono text-[11px] tracking-wider text-muted-foreground/80 mb-8 sm:mb-10 border-t border-b border-border py-3 flex flex-wrap gap-x-4 gap-y-1">
              <span>{lab.pi}</span>
              <span className="opacity-40">--</span>
              <span>{lab.established}</span>
              <span className="opacity-40">--</span>
              <span>{lab.funding}</span>
            </div>

            <div className="mb-10">
              <div className="font-mono text-[11px] tracking-[0.2em] text-muted-foreground mb-5">
                
              </div>
              <ul className="space-y-4">
                {lab.projects.map((project, i) => <li key={i} data-index={i} className="flex gap-3 group/project">
                    <ArrowRight className="size-4 mt-1 shrink-0 transition-transform group-hover/project:translate-x-1" style={{
                  color: lab.accentVar
                }} aria-hidden="true" />
                    <div>
                      <div className="text-[15px] font-medium text-foreground leading-snug">
                        {project.title}
                      </div>
                      <div className="text-sm text-muted-foreground leading-snug mt-0.5">
                        {project.description}
                      </div>
                    </div>
                  </li>)}
              </ul>
            </div>

            <Button asChild variant="outline" size="lg" className="group/btn bg-transparent border-border hover:bg-transparent" style={{
            borderColor: lab.accentVar,
            color: lab.accentVar
          }}>
              <Link to="/GetAQuotePage">
                <span className="font-mono text-xs tracking-wider">
                  REQUEST A QUOTE
                </span>
                <ArrowUpRight className="size-4 transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>;
}
export default function RecentTransformations() {
  return <section data-section-id="402" id="recent-transformations" className="dark bg-background text-foreground relative overflow-hidden">
      {}
      <div className="px-6 sm:px-10 lg:px-16 pt-20 sm:pt-28 lg:pt-32 pb-16 sm:pb-20 lg:pb-24 max-w-7xl">
        <div className="font-mono text-xs tracking-[0.2em] text-primary mb-8">
          
        </div>
        <h2 className="font-serif font-default font-light text-6xl sm:text-8xl lg:text-[160px] leading-[0.92] tracking-[-0.03em] text-foreground mb-10">
          Our Craft
        </h2>
        <p className="text-lg sm:text-xl lg:text-2xl text-muted-foreground max-w-2xl leading-relaxed font-serif font-default italic">
          Professional residential painting services. Precision, quality, and a commitment to excellence in every brushstroke.
        </p>
      </div>

      {}
      <div>
        {labs.map((lab, i) => <div data-index={i} key={lab.code}>
            <LabModule lab={lab} index={i} total={labs.length} />
            {i < labs.length - 1 && <div className="relative px-6 sm:px-10 lg:px-16 py-6 bg-background">
                <div className="flex items-center gap-4">
                  <div className="h-px flex-1" style={{
              backgroundColor: "var(--primary)"
            }} />
                  <span className="font-mono text-[11px] tracking-[0.2em] text-primary whitespace-nowrap">
                    ---- {String(i + 2).padStart(2, "0")} /{" "}
                    {String(labs.length).padStart(2, "0")} ----
                  </span>
                  <div className="h-px flex-1" style={{
              backgroundColor: "var(--primary)"
            }} />
                </div>
              </div>}
          </div>)}
      </div>

      {}
      <div className="px-6 sm:px-10 lg:px-16 py-16 border-t border-border">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 max-w-7xl">
          <div className="font-mono text-[11px] tracking-[0.2em] text-muted-foreground">
            
          </div>
          <Button asChild variant="ghost" size="lg" className="group/all hover:bg-transparent text-foreground hover:text-primary">
            <Link to="/GalleryPage">
              <span className="font-mono text-xs tracking-wider">
                VIEW FULL GALLERY
              </span>
              <ArrowUpRight className="size-4 transition-transform group-hover/all:translate-x-0.5 group-hover/all:-translate-y-0.5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}